'use client';

import React from 'react';
import { decodePassphrase } from '@/lib/client-utils';
import { DebugMode } from '@/lib/Debug';
import { KeyboardShortcuts } from '@/lib/KeyboardShortcuts';
import { RecordingIndicator } from '@/lib/RecordingIndicator';
import { RecordingPreparingBanner } from '@/lib/RecordingPreparingBanner';
import { RecordingRealtimeListener } from '@/lib/RecordingRealtimeListener';
import { RecordingStartFailedNotice } from '@/lib/RecordingStartFailedNotice';
import { SettingsMenu } from '@/lib/SettingsMenu';
import { ConnectionDetails } from '@/lib/types';
import { resolveBackupCodec, resolveVideoPublishCodec } from '@/lib/videoCodecPreferences';
import {
  formatChatMessageLinks,
  LocalUserChoices,
  RoomContext,
} from '@livekit/components-react';
import {
  DefaultReconnectPolicy,
  ExternalE2EEKeyProvider,
  RoomOptions,
  VideoCodec,
  Room,
  DeviceUnsupportedError,
  RoomConnectOptions,
  RoomEvent,
  TrackPublishDefaults,
  VideoCaptureOptions,
  ParticipantEvent,
  LocalTrackPublication,
  TrackPublication,
  LocalVideoTrack,
  Track,
  DisconnectReason,
  ConnectionState,
} from 'livekit-client';
import { useRouter } from 'next/navigation';
import { useSetupE2EE } from '@/lib/useSetupE2EE';
import { shouldRefreshJwt } from '@/lib/token-refresh';
import { applyParticipantTokenRefresh } from '@/lib/livekit-participant-token-refresh';
import {
  attemptMeetManualReconnect,
  connectRoomWithRetry,
  isManualReconnectReason,
  isSignalOrNetworkConnectError,
} from '@/lib/meet-reconnect';
import {
  buildRtcConfigForIcePolicy,
  markForceRelaySession,
  resolveForceRelay,
} from '@/lib/ice-path-policy';
import { useLowCPUOptimizer } from '@/lib/usePerfomanceOptimiser';
import { useSubscriberNetworkAdaptation } from '@/lib/useSubscriberNetworkAdaptation';
import { useRemoteAudioWatchdog } from '@/lib/useRemoteAudioWatchdog';
import { attachSubscriptionDiagnostics } from '@/lib/subscription-diagnostics';
import { useMeetingTelemetry } from '@/lib/useMeetingTelemetry';
import {
  useCustomBackgroundImage,
  CUSTOM_BACKGROUND_PREFIX,
} from '@/hooks/useCustomBackgroundImage';
import {
  applyBackgroundProcessorToPublication,
  BACKGROUND_FAILSAFE_EVENT,
  planBackgroundFailsafeRetry,
  type BackgroundType,
} from '@/lib/background-processor';
import {
  beginMediaMutationGuard,
  endMediaMutationGuard,
  isMediaMutationSuppressed,
  stripMediaTogglesIfSuppressed,
} from '@/lib/media-mutation-guard';
import {
  classifySettingsSaveIntent,
  shouldApplyDeviceMediaFromSettings,
} from '@/lib/settings-media-intent';
import {
  VirtualBackgroundProvider,
  VirtualBackgroundSync,
} from '@/lib/VirtualBackgroundContext';
import { RoomErrorBoundary } from '@/lib/RoomErrorBoundary';
import toast from 'react-hot-toast';
import {
  EconomyModeProvider,
  EconomyModeRoomBridge,
} from '@/lib/economyMode';
import { ScreenShareDefaultsBridge } from '@/lib/ScreenShareDefaultsBridge';
import { ScreenQualityProvider } from '@/lib/ScreenQualityContext';
import { useLargeRoomAdaptation } from '@/lib/useLargeRoomAdaptation';
import { ConnectionStabilityMonitor } from '@/lib/ConnectionStabilityMonitor';
import { ReconnectingOverlay } from '@/lib/ReconnectingOverlay';
import { useMeetingTracker } from '@/hooks/useMeetingTracker';
import {
  setCameraEnabledWithRetry,
  setMicrophoneEnabledWithRetry,
} from '@/lib/cameraPublishRetry';
import {
  mediaToRestoreAfterReconnect,
  startRoomAudioIfBlocked,
} from '@/lib/audio-playback-unlock';
import {
  buildSafeRoomAudioCaptureDefaults,
  buildSafeRoomVideoCaptureDefaults,
  isInvalidMediaConstraintError,
} from '@/lib/media-capture-constraints';
import { useReliableCameraEnable } from '@/lib/useReliableCameraEnable';
import {
  beginCameraSoftStart,
  MEDIA_POST_CONNECT_SETTLE_MS,
} from '@/lib/media-soft-start';
import { PreJoin } from './Prejoin';
import { InjectRecordIntoBar } from '@/lib/InjectRecordIntoBar';
import { InjectLayoutIntoBar } from '@/lib/InjectLayoutIntoBar';
import { InjectHandRaiseIntoBar } from '@/lib/InjectHandRaiseIntoBar';
import { InjectMoreOptionsIntoBar } from '@/lib/InjectMoreOptionsIntoBar';
import { MobileControlBar } from '@/lib/MobileControlBar';
import { HandRaiseProvider } from '@/lib/HandRaiseContext';
import { HandRaiseQueueProvider } from '@/lib/HandRaiseQueueContext';
import { HostControlProvider } from '@/lib/HostControlContext';
import { HostSettingsProvider } from '@/lib/HostSettingsContext';
import { HostControlFeedback } from '@/lib/HostControlFeedback';
import { InjectParticipantsIntoBar } from '@/lib/InjectParticipantsIntoBar';
import { InjectCustomChatMessages } from '@/lib/InjectCustomChatMessages';
import {
  MicrophonePermissionProvider,
  useMicrophonePermission,
  MicrophonePermissionEffects,
} from '@/lib/MicrophonePermissionContext';
import { AudioActivationNotifier } from '@/lib/AudioActivationNotifier';
import { AudioAutoplayBanner } from '@/lib/AudioAutoplayBanner';
import { PostMeetingFeedbackModal } from '@/components/PostMeetingFeedbackModal';
import { ReactionsProvider } from '@/lib/ReactionsContext';
import { ReactionsListener } from '@/lib/ReactionsListener';
import { ReactionsDisplay } from '@/lib/ReactionsDisplay';
import { InjectReactionsIntoBar } from '@/lib/InjectReactionsIntoBar';
import { PollProvider } from '@/lib/PollContext';
import { PollWidget, PollManagementModal } from '@/lib/PollComponents';
import { InjectPollButton } from '@/lib/InjectPollButton';
import { EvoluVideoConference } from '@/lib/EvoluVideoConference';
import { LayoutViewProvider } from '@/lib/LayoutContext';
import { BreakoutProvider } from '@/lib/BreakoutContext';
import { BreakoutControlPanel, BreakoutStatusBadge } from '@/lib/BreakoutComponents';
import { InjectStreamButton, StreamControlModal } from '@/lib/StreamComponents';
import { StreamProvider } from '@/lib/StreamContext';
import { InjectPipButton } from '@/lib/InjectPipButton';
import { ControlBarAutoHide } from '@/lib/ControlBarAutoHide';
import { NoiseCancellationProvider } from '@/lib/NoiseCancellationContext';
import { getVideoQualityRoomCapture } from '@/lib/video-quality-profile';
import { getScreenSharePublishDefaults } from '@/lib/screen-share-profile';
import { MEETING_AUDIO_PRESET } from '@/lib/audio-publish-preset';
import {
  applyVideoQualityToLocalCamera,
  VideoQualityProvider,
  useVideoQuality,
} from '@/lib/VideoQualityContext';
import type { VideoQualityProfile } from '@/lib/video-quality-profile';

export { applyBackgroundProcessorToPublication } from '@/lib/background-processor';

const CONN_DETAILS_ENDPOINT =
  process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT ?? '/api/connection-details';

const SHOW_SETTINGS_MENU = process.env.NEXT_PUBLIC_SHOW_SETTINGS_MENU == 'true';

const FEEDBACK_FORM_URL =
  process.env.NEXT_PUBLIC_POST_CALL_FEEDBACK_FORM_URL ||
  'https://docs.google.com/forms/d/e/1FAIpQLSd0xe57v_cMsyzvJ0JY2h23EamEX2RJpJhSaJceBQ7FWWjwTg/viewform?embedded=true';


interface BackgroundSettings {
  backgroundType?: unknown;
  virtualBackgroundImagePath?: string | null;
}

interface SavedSettings extends BackgroundSettings {
  audioEnabled?: boolean;
  videoEnabled?: boolean;
  audioDeviceId?: string;
  videoDeviceId?: string;
  audioOutputDeviceId?: string;
  __source?: 'device-sync' | 'background-apply';
}

// --- FUNÇÃO AUXILIAR PARA TROCA DE SALA ---
async function fetchToken(roomName: string, username: string, isHost: boolean, region?: string) {
  const url = new URL(CONN_DETAILS_ENDPOINT, window.location.origin);
  url.searchParams.append('roomName', roomName);
  url.searchParams.append('participantName', username);
  if (isHost) url.searchParams.append('host', 'true');
  if (region) url.searchParams.append('region', region);
  const resp = await fetch(url.toString());
  return await resp.json();
}

function isBackgroundType(value: unknown): value is BackgroundType {
  return value === 'none' || value === 'blur' || value === 'image';
}

function normalizeBackground(
  settings: BackgroundSettings | null | undefined,
  resolvePath: (value?: string | null) => string | undefined,
) {
  const type: BackgroundType = isBackgroundType(settings?.backgroundType)
    ? (settings!.backgroundType as BackgroundType)
    : 'none';
  const rawPath =
    type === 'image' && settings?.virtualBackgroundImagePath
      ? settings.virtualBackgroundImagePath
      : undefined;
  const imagePath = rawPath ? (resolvePath(rawPath) ?? rawPath) : undefined;
  return { type, imagePath };
}

export default function PageClientImpl(props: {
  roomName: string;
  region?: string;
  hq: boolean;
  initialVideoQuality?: VideoQualityProfile;
  codec?: VideoCodec;
  isHost?: boolean;
}) {
  const [preJoinChoices, setPreJoinChoices] = React.useState<LocalUserChoices | undefined>(
    undefined,
  );
  // Estado da conexão ativa (Pode ser Main ou Breakout)
  const [currentConnection, setCurrentConnection] = React.useState<ConnectionDetails | undefined>(
    undefined,
  );

  const preJoinDefaults = React.useMemo(() => {
    return {
      username: '',
      videoEnabled: false,
      audioEnabled: true,
    };
  }, []);

  const handlePreJoinSubmit = React.useCallback(
    async (values: LocalUserChoices) => {
      setPreJoinChoices(values);
      // Busca token inicial
      const data = await fetchToken(props.roomName, values.username, props.isHost === true, props.region);
      setCurrentConnection(data);
    },
    [props.roomName, props.region, props.isHost],
  );
  
  const handlePreJoinError = React.useCallback((e: any) => console.error(e), []);

  // --- LÓGICA DE TROCA DE SALA (Salas de Apoio) ---
  const roomChangeGenerationRef = React.useRef(0);

  const handleRoomChange = React.useCallback(async (targetRoomId: string | null) => {
    if (!preJoinChoices) return;

    const targetName = targetRoomId || props.roomName;
    const generation = ++roomChangeGenerationRef.current;
    console.log(`PageClientImpl: Trocando para sala ${targetName}`);

    setCurrentConnection(undefined);

    try {
      const newData = await fetchToken(
        targetName,
        preJoinChoices.username,
        props.isHost === true,
        props.region,
      );
      if (roomChangeGenerationRef.current !== generation) {
        return;
      }
      setCurrentConnection(newData);
    } catch (e) {
      if (roomChangeGenerationRef.current !== generation) {
        return;
      }
      console.error('Erro ao trocar de sala:', e);
      alert('Não foi possível conectar à sala de apoio.');
    }
  }, [props.roomName, props.isHost, props.region, preJoinChoices]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (props.isHost) {
        window.sessionStorage.setItem('evolutto-force-host', 'true');
      } else {
        window.sessionStorage.removeItem('evolutto-force-host');
      }
    } catch (error) {
      console.warn(
        'PageClientImpl: não foi possível atualizar flag de anfitrião no sessionStorage:',
        error,
      );
    }
    return () => {
      try {
        window.sessionStorage.removeItem('evolutto-force-host');
      } catch (cleanupError) {
        console.warn(
          'PageClientImpl: não foi possível limpar flag de anfitrião no sessionStorage:',
          cleanupError,
        );
      }
    };
  }, [props.isHost]);

  const initialVideoQuality =
    props.initialVideoQuality ?? (props.hq ? 'max' : undefined);

  return (
    <VideoQualityProvider initialProfile={initialVideoQuality}>
    <ScreenQualityProvider>
    <main className="h-screen w-screen">
      {currentConnection === undefined && preJoinChoices === undefined ? (
        <div className="grid place-items-center h-screen">
          <PreJoin
            defaults={preJoinDefaults}
            roomName={props.roomName}
            onError={handlePreJoinError}
            onSubmit={handlePreJoinSubmit}
            joinLabel="Join Room"
            isHost={props.isHost}
          />
        </div>
      ) : currentConnection ? (
        <VideoConferenceComponent
          // A chave é crucial: quando o token muda (nova sala), recria o componente do zero
          key={currentConnection.participantToken} 
          connectionDetails={currentConnection}
          userChoices={preJoinChoices!}
          isHost={props.isHost === true}
          options={{ codec: props.codec }}
          onRoomChange={handleRoomChange}
        />
      ) : (
        <div className="grid place-items-center h-screen bg-[#111] text-white">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <p>Conectando...</p>
          </div>
        </div>
      )}
    </main>
    </ScreenQualityProvider>
    </VideoQualityProvider>
  );
}

export function VideoConferenceComponent(props: {
  userChoices: LocalUserChoices;
  connectionDetails: ConnectionDetails;
  options: {
    codec?: VideoCodec;
  };
  isHost?: boolean;
  onRoomChange: (room: string | null) => void;
}) {
  const { videoQualityProfile } = useVideoQuality();
  const { worker, e2eePassphrase } = useSetupE2EE();
  const e2eeEnabled = !!(e2eePassphrase && worker);

  // Inicializar tracker de reunião
  useMeetingTracker();

  const keyProvider = React.useMemo(() => new ExternalE2EEKeyProvider(), []);

  const [e2eeSetupComplete, setE2eeSetupComplete] = React.useState(false);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const [microphonePermissionDenied, setMicrophonePermissionDenied] = React.useState(false);
  const microphonePermission = React.useMemo(
    () => ({
      denied: microphonePermissionDenied,
      setDenied: setMicrophonePermissionDenied,
    }),
    [microphonePermissionDenied],
  );
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const [isConnectionRecovering, setIsConnectionRecovering] = React.useState(false);
  const feedbackPromptedRef = React.useRef(false);
  const hasNavigatedAfterFeedbackRef = React.useRef(false);
  const screenShareAttemptRef = React.useRef<number | null>(null);

  const roomOptions = React.useMemo((): RoomOptions => {
    const videoCodec = resolveVideoPublishCodec({
      requestedCodec: props.options.codec,
      e2eeEnabled,
    });
    const backupCodec = resolveBackupCodec(videoCodec);
    const qualityCapture = getVideoQualityRoomCapture(videoQualityProfile);
    // Screen share: default Normal (até 1080p / ≤1,5 Mbps / 1 camada). Capture resolution is
    // injected via ScreenShareDefaultsBridge (ControlBar does not accept captureOptions).
    const screenSharePublish = getScreenSharePublishDefaults();
    // deviceId string 'default' vira { exact: 'default' } no livekit-client e causa
    // "Invalid constraint" em mobile/Safari — sanitizar para ideal / omitir.
    const videoCaptureDefaults: VideoCaptureOptions = buildSafeRoomVideoCaptureDefaults({
      resolution: qualityCapture.videoCaptureDefaults.resolution,
      deviceId: props.userChoices.videoDeviceId ?? undefined,
    });
    const publishDefaults: TrackPublishDefaults = {
      simulcast: true,
      // Voz: Opus 32 kbps mono é transparente para fala (Meet usa ~32 kbps); o default
      // do SDK (`music`, 48 kbps) + RED (2 cópias) chegava a ~100 kbps de uplink que
      // compete com câmera + tela em links residenciais.
      audioPreset: MEETING_AUDIO_PRESET,
      // DTX pauses the audio stream during silence, saving ~40-60 % of audio bandwidth.
      dtx: true,
      videoEncoding: qualityCapture.publishDefaults.videoEncoding,
      videoSimulcastLayers: qualityCapture.publishDefaults.videoSimulcastLayers,
      red: !e2eeEnabled,
      videoCodec,
      ...(backupCodec ? { backupCodec } : {}),
      screenShareEncoding: screenSharePublish.screenShareEncoding,
      // Empty = single screenshare layer (no 1080+540 dual upload).
      screenShareSimulcastLayers: screenSharePublish.screenShareSimulcastLayers,
    };

    return {
      videoCaptureDefaults: videoCaptureDefaults,
      publishDefaults: publishDefaults,
      audioCaptureDefaults: buildSafeRoomAudioCaptureDefaults({
        deviceId: props.userChoices.audioDeviceId ?? undefined,
      }),
      adaptiveStream: true,
      dynacast: true,
      stopLocalTrackOnUnpublish: true,
      reconnectPolicy: new DefaultReconnectPolicy(),
      e2ee: keyProvider && worker && e2eeEnabled ? { keyProvider, worker } : undefined,
    };
  }, [
    props.userChoices,
    videoQualityProfile,
    props.options.codec,
    e2eeEnabled,
    keyProvider,
    worker,
  ]);

  const resolvedVideoCodec = React.useMemo(
    () =>
      resolveVideoPublishCodec({
        requestedCodec: props.options.codec,
        e2eeEnabled,
      }),
    [props.options.codec, e2eeEnabled],
  );

  const roomInstanceKey = React.useMemo(
    () =>
      [
        props.connectionDetails.participantToken,
        String(e2eeEnabled),
        videoQualityProfile,
        resolvedVideoCodec,
        props.userChoices.videoDeviceId ?? '',
        props.userChoices.audioDeviceId ?? '',
      ].join('\0'),
    [
      props.connectionDetails.participantToken,
      e2eeEnabled,
      videoQualityProfile,
      resolvedVideoCodec,
      props.userChoices.videoDeviceId,
      props.userChoices.audioDeviceId,
    ],
  );

  // roomOptions is fully determined by roomInstanceKey inputs + e2ee objects; avoid
  // listing roomOptions here or the Room would be recreated every render.
  const room = React.useMemo(() => new Room(roomOptions), [roomInstanceKey]); // eslint-disable-line react-hooks/exhaustive-deps -- roomOptions tied to roomInstanceKey

  const { resolveCustomBackgroundPath, getCustomBackgroundIdByDataUrl } =
    useCustomBackgroundImage();

  const initialBackground = React.useMemo(
    () => normalizeBackground(props.userChoices as BackgroundSettings, resolveCustomBackgroundPath),
    [props.userChoices, resolveCustomBackgroundPath],
  );

  React.useEffect(() => {
    latestBackgroundRef.current = initialBackground;
  }, [initialBackground]);

  const backgroundApplyLockRef = React.useRef(false);
  const pendingBackgroundRef = React.useRef<{ type: BackgroundType; imagePath?: string } | null>(
    null,
  );
  /** Evita fundo virtual durante soft-start (applyConstraints + processor competem). */
  const cameraSoftStartActiveRef = React.useRef(false);
  // Inicializa com o background persistido para que LocalTrackPublished o reaplique imediatamente
  // (o useEffect abaixo apenas sincroniza quando initialBackground muda após o mount)
  const latestBackgroundRef = React.useRef<{ type: BackgroundType; imagePath?: string } | null>(
    initialBackground,
  );
  const setVirtualBackgroundTypeRef = React.useRef<(type: BackgroundType) => void>(() => {});
  const registerVirtualBackgroundSetter = React.useCallback((setter: (type: BackgroundType) => void) => {
    setVirtualBackgroundTypeRef.current = setter;
  }, []);
  const deviceSettingsRef = React.useRef<SavedSettings>({
    audioEnabled: props.userChoices.audioEnabled,
    videoEnabled: props.userChoices.videoEnabled,
    audioDeviceId: props.userChoices.audioDeviceId,
    videoDeviceId: props.userChoices.videoDeviceId,
    audioOutputDeviceId: (props.userChoices as any)?.audioOutputDeviceId ?? 'default',
  });

  React.useEffect(() => {
    latestBackgroundRef.current = normalizeBackground(
      props.userChoices as BackgroundSettings,
      resolveCustomBackgroundPath,
    );
    deviceSettingsRef.current = {
      audioEnabled: props.userChoices.audioEnabled,
      videoEnabled: props.userChoices.videoEnabled,
      audioDeviceId: props.userChoices.audioDeviceId,
      videoDeviceId: props.userChoices.videoDeviceId,
      audioOutputDeviceId: (props.userChoices as any)?.audioOutputDeviceId ?? 'default',
    };
  }, [props.userChoices, resolveCustomBackgroundPath]);

  const persistDeviceSettings = React.useCallback((update: Partial<SavedSettings>) => {
    if (typeof window === 'undefined') {
      return;
    }

    const sanitized = stripMediaTogglesIfSuppressed(update, isMediaMutationSuppressed());
    if (!sanitized) {
      return;
    }
    update = sanitized;

    try {
      const previous = deviceSettingsRef.current;
      let hasChange = false;
      for (const key of Object.keys(update) as Array<keyof SavedSettings>) {
        if (update[key] !== undefined && previous[key] !== update[key]) {
          hasChange = true;
          break;
        }
      }
      if (!hasChange) {
        return;
      }

      const merged = {
        ...previous,
        ...update,
      };
      deviceSettingsRef.current = merged;

      let stored: SavedSettings = {};
      try {
        const raw = window.localStorage.getItem('evolutto-user-settings');
        stored = raw ? (JSON.parse(raw) as SavedSettings) : {};
      } catch (error) {
        console.warn('PageClientImpl: não foi possível ler configurações persistidas:', error);
      }

      const background = latestBackgroundRef.current;
      const persistedVirtualPath =
        background && background.type === 'image' && background.imagePath
          ? (() => {
              const id = getCustomBackgroundIdByDataUrl(background.imagePath);
              return id ? `${CUSTOM_BACKGROUND_PREFIX}${id}` : background.imagePath;
            })()
          : background && background.type === 'image'
            ? ''
            : (background?.imagePath ?? null);

      const payload: SavedSettings = {
        ...stored,
        ...merged,
        backgroundType: background?.type,
        virtualBackgroundImagePath: persistedVirtualPath,
        __source: 'device-sync',
      };

      window.localStorage.setItem('evolutto-user-settings', JSON.stringify(payload));
      window.dispatchEvent(
        new CustomEvent<SavedSettings>('settings-saved', {
          detail: payload,
        }),
      );
    } catch (error) {
      console.error('PageClientImpl: erro ao persistir configurações:', error);
    }
  }, []);

  const applyDeviceSettings = React.useCallback(
    async (settings: SavedSettings) => {
      if (!room || room.state !== 'connected') {
        return;
      }

      const operations: Array<Promise<void>> = [];

      if (typeof settings.audioEnabled === 'boolean') {
        // Verificar o estado atual do microfone antes de aplicar
        const micPublication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        const currentMicState = micPublication ? !micPublication.isMuted : undefined;
        
        // Só aplicar se o estado for diferente do atual
        if (currentMicState !== settings.audioEnabled) {
          console.log(
            `PageClientImpl: Aplicando configuração de microfone: ${settings.audioEnabled} (atual: ${currentMicState})`,
          );
          operations.push(
            setMicrophoneEnabledWithRetry(room.localParticipant, settings.audioEnabled, {
              logLabel: 'applyDeviceSettings',
            })
              .catch((error) => {
                console.error('PageClientImpl: erro ao ajustar microfone:', error);
              })
              .then(() => {}),
          );
        } else {
          console.log(
            `PageClientImpl: Pulando aplicação de microfone - estado já está correto (${settings.audioEnabled})`,
          );
        }
      }

      // Só aplicar videoEnabled se estiver explicitamente definido e for diferente do atual
      // Isso evita desligar a câmera acidentalmente quando apenas salvamos o background
      if (typeof settings.videoEnabled === 'boolean') {
        // Verificar o estado atual da câmera antes de aplicar
        const camPublication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const currentCamState = camPublication ? !camPublication.isMuted : undefined;
        
        // Só aplicar se o estado for diferente do atual
        if (currentCamState !== settings.videoEnabled) {
          console.log(
            `PageClientImpl: Aplicando configuração de câmera: ${settings.videoEnabled} (atual: ${currentCamState})`,
          );
          operations.push(
            setCameraEnabledWithRetry(room.localParticipant, settings.videoEnabled, {
              logLabel: 'applyDeviceSettings',
            })
              .catch((error) => {
                console.error('PageClientImpl: erro ao ajustar câmera:', error);
              })
              .then(() => {}),
          );
        } else {
          console.log(
            `PageClientImpl: Pulando aplicação de câmera - estado já está correto (${settings.videoEnabled})`,
          );
        }
      } else {
        // Se videoEnabled não está definido, manter o estado atual da câmera
        console.log('PageClientImpl: videoEnabled não definido nas configurações, mantendo estado atual da câmera');
      }

      if (settings.audioDeviceId) {
        operations.push(
          room
            .switchActiveDevice('audioinput', settings.audioDeviceId)
            .catch((error) => {
              console.error('PageClientImpl: erro ao trocar dispositivo de microfone:', error);
            })
            .then(() => {}),
        );
      }

      if (settings.videoDeviceId) {
        operations.push(
          room
            .switchActiveDevice('videoinput', settings.videoDeviceId)
            .catch((error) => {
              console.error('PageClientImpl: erro ao trocar dispositivo de câmera:', error);
            })
            .then(() => {}),
        );
      }

      if (settings.audioOutputDeviceId) {
        operations.push(
          room
            .switchActiveDevice(
              'audiooutput',
              settings.audioOutputDeviceId,
              settings.audioOutputDeviceId !== 'default',
            )
            .catch((error) => {
              console.warn('PageClientImpl: não foi possível trocar saída de áudio:', error);
            })
            .then(() => {}),
        );
      }

      if (operations.length > 0) {
        await Promise.all(operations);
      }
    },
    [room],
  );

  const applyBackgroundToLocalParticipant = React.useCallback(
    async (type: BackgroundType, imagePath?: string) => {
      latestBackgroundRef.current = { type, imagePath };

      if (backgroundApplyLockRef.current) {
        pendingBackgroundRef.current = { type, imagePath };
        return;
      }

      if (!room || room.state !== 'connected') {
        console.log('PageClientImpl: Room não está conectado ao aplicar fundo');
        return;
      }

      const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

      backgroundApplyLockRef.current = true;
      beginMediaMutationGuard();

      try {
        const publications = Array.from(room.localParticipant.videoTrackPublications.values());
        if (publications.length === 0) {
          console.log('PageClientImpl: Nenhum track de vídeo local para aplicar fundo');
          return;
        }

        const targetType: BackgroundType = type === 'image' && !imagePath ? 'none' : type;
        const targetImage = targetType === 'image' ? imagePath : undefined;
        const shouldApplyProcessor = targetType === 'blur' || targetType === 'image';
        setVirtualBackgroundTypeRef.current(targetType);

        for (const publication of publications) {
          await applyBackgroundProcessorToPublication(publication, {
            targetType,
            targetImage,
            shouldApplyProcessor,
            delay,
          });
        }
      } finally {
        backgroundApplyLockRef.current = false;
        endMediaMutationGuard();
        const pending = pendingBackgroundRef.current;
        if (pending) {
          pendingBackgroundRef.current = null;
          void applyBackgroundToLocalParticipant(pending.type, pending.imagePath);
        }
      }
    },
    [room],
  );

  const clearVirtualBackgroundForEconomyRef = React.useRef<() => void>(() => {});
  React.useEffect(() => {
    clearVirtualBackgroundForEconomyRef.current = () => {
      setVirtualBackgroundTypeRef.current('none');
      void applyBackgroundToLocalParticipant('none');
    };
  }, [applyBackgroundToLocalParticipant]);

  React.useEffect(() => {
    if (e2eeEnabled) {
      keyProvider
        .setKey(decodePassphrase(e2eePassphrase))
        .then(() => {
          room.setE2EEEnabled(true).catch((e) => {
            if (e instanceof DeviceUnsupportedError) {
              alert(
                `You're trying to join an encrypted meeting, but your browser does not support it. Please update it to the latest version and try again.`,
              );
              console.error(e);
            } else {
              throw e;
            }
          });
        })
        .then(() => setE2eeSetupComplete(true));
    } else {
      setE2eeSetupComplete(true);
    }
  }, [e2eeEnabled, room, e2eePassphrase]);

  const router = useRouter();
  const buildMediaErrorMessage = React.useCallback((error: Error) => {
    if (error instanceof DOMException) {
      switch (error.name) {
        case 'NotAllowedError':
          return 'O navegador bloqueou o acesso à câmera ou ao microfone. Libere a permissão e tente novamente.';
        case 'NotFoundError':
          return 'Não encontramos nenhum dispositivo de câmera ou microfone disponível. Conecte um dispositivo e recarregue a página.';
        case 'NotReadableError':
        case 'TrackStartError':
          return 'Não conseguimos acessar sua câmera ou microfone. Feche outros aplicativos que possam estar usando o dispositivo e tente novamente.';
        case 'OverconstrainedError':
          return 'Não foi possível ligar a câmera ou o microfone com as configurações atuais. Tente outra câmera/microfone ou recarregue a página.';
        case 'SecurityError':
          return 'O navegador bloqueou o acesso por configuração de segurança. Verifique bloqueadores de conteúdo ou políticas corporativas.';
        default:
          break;
      }
    }

    const lowerMessage = error.message?.toLowerCase() ?? '';
    if (isSignalOrNetworkConnectError(error)) {
      return 'Não conseguimos conectar ao servidor da reunião. Verifique sua internet e aguarde — estamos tentando reconectar automaticamente.';
    }
    if (lowerMessage.includes('allocate video')) {
      return 'Não foi possível iniciar a câmera. Feche outras abas que usem o vídeo ou escolha outra câmera nas configurações do navegador.';
    }
    if (isInvalidMediaConstraintError(error)) {
      return 'Não foi possível ligar a câmera ou o microfone com as configurações atuais. Tente outra câmera/microfone ou recarregue a página.';
    }

    return 'Tivemos um problema ao acessar sua câmera ou microfone. Atualize a página após conferir permissões e dispositivos.';
  }, []);

  // Self-hosted: TURN vem no join (turn-sp / UDP 3478). Em multi-homing (Docker+eth+wifi)
  // forçamos relay para evitar ICE flap → BWE colapsado (270p@5fps). Ver ice-path-policy.ts.
  const [icePolicy, setIcePolicy] = React.useState<{
    ready: boolean;
    forceRelay: boolean;
    reason: string;
  }>({ ready: false, forceRelay: false, reason: 'pending' });

  React.useEffect(() => {
    let cancelled = false;
    void resolveForceRelay({
      search: typeof window !== 'undefined' ? window.location.search : '',
      storageGet: (key) => {
        try {
          return window.localStorage.getItem(key);
        } catch {
          return null;
        }
      },
    }).then((result) => {
      if (cancelled) return;
      setIcePolicy({
        ready: true,
        forceRelay: result.forceRelay,
        reason: result.reason,
      });
      markForceRelaySession(result.forceRelay);
      if (result.forceRelay) {
        console.info(
          `[ice-path] forçando TURN relay (reason=${result.reason}) — evita flap ICE em multi-NIC/Docker`,
        );
      }
    });
    return () => {
      cancelled = true;
      markForceRelaySession(false);
    };
  }, []);

  const connectOptions = React.useMemo((): RoomConnectOptions => {
    const rtcConfig = buildRtcConfigForIcePolicy(icePolicy.forceRelay);
    return {
      autoSubscribe: true,
      ...(rtcConfig ? { rtcConfig } : {}),
    };
  }, [icePolicy.forceRelay]);

  const openPostMeetingFeedback = React.useCallback(async () => {
    if (feedbackPromptedRef.current) {
      return;
    }
    feedbackPromptedRef.current = true;

    const shouldDisconnect = room && (room.state === 'connected' || room.state === 'connecting');
    if (shouldDisconnect) {
      setIsDisconnecting(true);
    }

    try {
      if (shouldDisconnect) {
        await room.disconnect();
      }
    } catch (error) {
      console.error('Erro ao desconectar room:', error);
    } finally {
      setIsDisconnecting(false);
      setShowFeedbackModal(true);
    }
  }, [room]);

  const handleRoomDisconnected = React.useCallback(
    (reason?: DisconnectReason) => {
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        void openPostMeetingFeedback();
        return;
      }

      if (reason !== undefined) {
        console.warn('LiveKit disconnected (não iniciado pelo cliente):', reason);
      }

      if (!isManualReconnectReason(reason)) {
        void openPostMeetingFeedback();
        return;
      }

      void (async () => {
        setIsConnectionRecovering(true);
        const { roomName, participantName } = props.connectionDetails;
        const isHostUser = props.isHost === true;
        const region = (props.userChoices as { region?: string }).region;

        const reconnected = await attemptMeetManualReconnect({
          room,
          connectionDetails: props.connectionDetails,
          connectOptions,
          fetchFreshToken: () =>
            fetchToken(roomName, participantName, isHostUser, region),
        });

        setIsConnectionRecovering(false);

        if (reconnected) {
          feedbackPromptedRef.current = false;
          console.info('[PageClientImpl] Reconexão manual bem-sucedida após queda');
          return;
        }

        void openPostMeetingFeedback();
      })();
    },
    [
      connectOptions,
      openPostMeetingFeedback,
      props.connectionDetails,
      props.isHost,
      props.userChoices,
      room,
    ],
  );
  const handleError = React.useCallback(
    (error: Error) => {
      if (error instanceof DOMException) {
        const name = error.name?.toLowerCase() ?? '';
        const message = error.message?.toLowerCase() ?? '';
        
        // PRIMEIRA VERIFICAÇÃO: Se há uma tentativa de screen share ativa, SEMPRE ignorar o erro
        // Isso previne mutar o microfone quando o usuário cancela o diálogo de screen share
        if (screenShareAttemptRef.current) {
          console.log('MediaDevices error occurred during/after screen share attempt, ignoring (not muting microphone)', {
            errorName: name,
            errorMessage: message,
            error
          });
          clearTimeout(screenShareAttemptRef.current);
          screenShareAttemptRef.current = null;
          return;
        }
        
        // AbortError geralmente indica que o usuário cancelou uma ação
        if (name === 'aborterror') {
          console.log('MediaDevices action cancelled by user', error);
          return;
        }
        
        // Verificar se o erro está relacionado a screen share (mesmo sem tentativa ativa)
        if (message.includes('screen') || message.includes('display') || message.includes('share')) {
          console.warn('Screen share error (not muting microphone)', error);
          return;
        }
        
        // Apenas mutar microfone se for um erro de permissão E não for relacionado a screen share
        if (name === 'notallowederror' || message.includes('permission denied')) {
          if (!message.includes('screen') && !message.includes('display') && !message.includes('share')) {
            const micPublication = Array.from(room.localParticipant.audioTrackPublications.values())[0];
            if (micPublication && micPublication.isMuted === false) {
              console.warn('Microphone permission denied', error);
              setMicrophonePermissionDenied(true);
              room.localParticipant
                .setMicrophoneEnabled(false)
                .catch((err) => console.error('Failed to disable microphone after denial', err));
              return;
            }
          }
        }
      }

      console.error(error);
      const userMessage = buildMediaErrorMessage(error);
      const technicalDetails = error.message ? `\n\nDetalhes técnicos: ${error.message}` : '';
      alert(`${userMessage}${technicalDetails}`);
    },
    [buildMediaErrorMessage, room, setMicrophonePermissionDenied],
  );

  useReliableCameraEnable(room, handleError);

  const handleEncryptionError = React.useCallback((error: Error) => {
    console.error(error);
    alert(
      `Encountered an unexpected encryption error, check the console logs for details: ${error.message}`,
    );
  }, []);
  const handleFeedbackModalClose = React.useCallback(() => {
    if (hasNavigatedAfterFeedbackRef.current) {
      return;
    }
    hasNavigatedAfterFeedbackRef.current = true;
    setShowFeedbackModal(false);
    router.push('/');
  }, [router]);

  const ensureLocalHostMetadata = React.useCallback(async () => {
    if (!props.isHost) {
      return;
    }

    const participant = room?.localParticipant;
    if (!participant) {
      return;
    }

    const currentMetadata = participant.metadata;
    let parsed: Record<string, any> = {};

    if (currentMetadata) {
      try {
        const candidate = JSON.parse(currentMetadata);
        if (candidate && typeof candidate === 'object') {
          parsed = candidate as Record<string, any>;
        }
      } catch (error) {
        console.warn(
          'PageClientImpl: não foi possível interpretar metadata atual do participante local:',
          error,
        );
      }
    }

    const needsIsHost = parsed.isHost !== true;
    const needsRole = parsed.role !== 'host';
    const needsForceHost = parsed.forceHost !== true;

    if (!needsIsHost && !needsRole && !needsForceHost) {
      return;
    }

    const updatedMetadata = {
      ...parsed,
      isHost: true,
      role: 'host',
      forceHost: true,
    };

    try {
      const serialized = JSON.stringify(updatedMetadata);
      if (serialized !== currentMetadata) {
        await participant.setMetadata(serialized);
      }
    } catch (error) {
      console.warn(
        'PageClientImpl: erro ao garantir metadata de anfitrião para participante local:',
        error,
      );
    }
  }, [props.isHost, room]);

  React.useEffect(() => {
    room.on(RoomEvent.Disconnected, handleRoomDisconnected);
    room.on(RoomEvent.EncryptionError, handleEncryptionError);
    room.on(RoomEvent.MediaDevicesError, handleError);

    // Espera a política ICE (probe multi-NIC/Docker) antes do connect — senão o BWE já nasce colapsado.
    let cancelledConnect = false;
    let cancelSoftStart: (() => void) | undefined;
    if (e2eeSetupComplete && icePolicy.ready) {
      const { serverUrl, participantToken, roomName, participantName } = props.connectionDetails;
      const isHostUser = props.isHost === true;
      const region = (props.userChoices as { region?: string }).region;

      const normalized = normalizeBackground(
        props.userChoices as BackgroundSettings,
        resolveCustomBackgroundPath,
      );
      latestBackgroundRef.current = normalized;
      deviceSettingsRef.current = {
        audioEnabled: props.userChoices.audioEnabled,
        videoEnabled: props.userChoices.videoEnabled,
        audioDeviceId: props.userChoices.audioDeviceId,
        videoDeviceId: props.userChoices.videoDeviceId,
        audioOutputDeviceId: (props.userChoices as any)?.audioOutputDeviceId ?? 'default',
      };

      // Importante: NÃO publicar câmera/mic em paralelo com o connect.
      // Isso inundava o uplink enquanto o ICE/TURN ainda alocava o path → travamento
      // nos primeiros segundos e estabilização depois.
      connectRoomWithRetry({
        room,
        serverUrl,
        token: participantToken,
        connectOptions,
        fetchFreshToken: () => fetchToken(roomName, participantName, isHostUser, region),
      })
        .then(async () => {
          if (cancelledConnect) return;
          void ensureLocalHostMetadata();
          void startRoomAudioIfBlocked(room);

          if (props.userChoices.audioEnabled) {
            await setMicrophoneEnabledWithRetry(room.localParticipant, true, {
              logLabel: 'connect',
            }).catch((error) => {
              handleError(error);
            });
          }

          // Deixa o path ICE/TURN assentar antes do vídeo pesado.
          await new Promise((r) => setTimeout(r, MEDIA_POST_CONNECT_SETTLE_MS));
          if (cancelledConnect) return;

          if (props.userChoices.videoEnabled) {
            await setCameraEnabledWithRetry(room.localParticipant, true, {
              logLabel: 'connect',
            }).catch((error) => {
              handleError(error);
            });
            if (!cancelledConnect) {
              cameraSoftStartActiveRef.current = true;
              const soft = beginCameraSoftStart(room);
              cancelSoftStart = () => {
                soft.cancel();
                cameraSoftStartActiveRef.current = false;
              };
              void soft.promise.finally(() => {
                cameraSoftStartActiveRef.current = false;
                if (!cancelledConnect) {
                  void applyBackgroundToLocalParticipant(normalized.type, normalized.imagePath);
                }
              });
            }
          } else if (!cancelledConnect && normalized.type !== 'none') {
            // Sem vídeo no join — fundo será aplicado ao ligar câmera (LocalTrackPublished).
          }

          if (!cancelledConnect) {
            void applyDeviceSettings(deviceSettingsRef.current);
          }
        })
        .catch((error) => {
          if (!cancelledConnect) {
            handleError(error);
          }
        });
    }
    return () => {
      cancelledConnect = true;
      cancelSoftStart?.();
      cameraSoftStartActiveRef.current = false;
      room.off(RoomEvent.Disconnected, handleRoomDisconnected);
      if (room.state !== ConnectionState.Disconnected) {
        room.disconnect().catch(() => {});
      }
      room.off(RoomEvent.EncryptionError, handleEncryptionError);
      room.off(RoomEvent.MediaDevicesError, handleError);
    };
  }, [
    e2eeSetupComplete,
    icePolicy.ready,
    icePolicy.forceRelay,
    room,
    props.connectionDetails,
    props.userChoices,
    applyBackgroundToLocalParticipant,
    applyDeviceSettings,
    handleRoomDisconnected,
    handleEncryptionError,
    handleError,
    connectOptions,
    ensureLocalHostMetadata,
  ]);

  React.useEffect(() => {
    if (!room) return;

    const handleReconnected = () => {
      if (room.state !== 'connected') return;
      void startRoomAudioIfBlocked(room);
      const restore = mediaToRestoreAfterReconnect(deviceSettingsRef.current);
      if (restore.restoreMic) {
        void setMicrophoneEnabledWithRetry(room.localParticipant, true, {
          logLabel: 'reconnect-mic',
        }).catch((err) => {
          console.warn(
            'PageClientImpl: não foi possível religar o microfone após reconexão:',
            err,
          );
        });
      }
      if (!restore.restoreCamera) return;
      void setCameraEnabledWithRetry(room.localParticipant, true, { logLabel: 'reconnect' }).catch(
        (err) => {
          console.warn(
            'PageClientImpl: não foi possível religar a câmera após reconexão:',
            err,
          );
        },
      );
    };

    room.on(RoomEvent.Reconnected, handleReconnected);

    return () => {
      room.off(RoomEvent.Reconnected, handleReconnected);
    };
  }, [room]);

  React.useEffect(() => {
    if (!room) {
      return;
    }

    let cancelled = false;
    const { roomName, participantName, participantToken } = props.connectionDetails;
    const isHostUser = props.isHost === true;
    const region = (props.userChoices as { region?: string }).region;

    const refreshTokenIfNeeded = async () => {
      if (cancelled || room.state !== ConnectionState.Connected) {
        return;
      }
      if (!shouldRefreshJwt(participantToken)) {
        return;
      }

      try {
        const newDetails = await fetchToken(roomName, participantName, isHostUser, region);
        if (cancelled || room.state !== ConnectionState.Connected) {
          return;
        }
        await applyParticipantTokenRefresh(
          room,
          newDetails.serverUrl,
          newDetails.participantToken,
          connectOptions,
        );
        console.log('[PageClientImpl] Token JWT renovado antes do vencimento');
      } catch (error) {
        console.warn('[PageClientImpl] Falha ao renovar token JWT:', error);
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshTokenIfNeeded();
    }, 60_000);
    void refreshTokenIfNeeded();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    room,
    props.connectionDetails,
    props.isHost,
    props.userChoices,
    connectOptions,
  ]);

  React.useEffect(() => {
    const participant = room?.localParticipant;
    if (!participant) {
      return;
    }

    const handleLocalTrackPublished = (publication: LocalTrackPublication) => {
      if (publication.kind !== Track.Kind.Video) {
        return;
      }
      if (publication.source !== Track.Source.Camera) {
        return;
      }
      // Evita reaplicar durante applyConstraints/setProcessor ou soft-start da câmera.
      if (backgroundApplyLockRef.current || cameraSoftStartActiveRef.current) {
        return;
      }
      const background = latestBackgroundRef.current;
      if (!background || background.type === 'none') return;
      const { type, imagePath } = background;
      void applyBackgroundToLocalParticipant(type, imagePath);
    };

    participant.on(ParticipantEvent.LocalTrackPublished, handleLocalTrackPublished);

    return () => {
      participant.off(ParticipantEvent.LocalTrackPublished, handleLocalTrackPublished);
    };
  }, [room, applyBackgroundToLocalParticipant]);

  React.useEffect(() => {
    if (!room) {
      return;
    }

    const handleActiveDeviceChanged = (kind: MediaDeviceKind, deviceId: string) => {
      const update: Partial<SavedSettings> = {};
      if (kind === 'audioinput') {
        update.audioDeviceId = deviceId;
      } else if (kind === 'videoinput') {
        update.videoDeviceId = deviceId;
      } else if (kind === 'audiooutput') {
        update.audioOutputDeviceId = deviceId;
      }

      if (Object.keys(update).length > 0) {
        persistDeviceSettings(update);
      }
    };

    room.on(RoomEvent.ActiveDeviceChanged, handleActiveDeviceChanged);
    return () => {
      room.off(RoomEvent.ActiveDeviceChanged, handleActiveDeviceChanged);
    };
  }, [room, persistDeviceSettings]);

  React.useEffect(() => {
    const participant = room?.localParticipant;
    if (!participant) {
      return;
    }

    const handleTrackMuted = (publication: TrackPublication) => {
      // Ignorar eventos de screen sharing e outros sources que não são microfone ou câmera
      if (
        publication.source === Track.Source.ScreenShare ||
        publication.source === Track.Source.ScreenShareAudio ||
        publication.source === Track.Source.Unknown
      ) {
        console.log(
          'PageClientImpl: Ignorando evento TrackMuted para source:',
          publication.source,
        );
        return;
      }

      if (isMediaMutationSuppressed()) {
        console.log(
          'PageClientImpl: Ignorando TrackMuted transitório durante apply de fundo/processor',
        );
        return;
      }

      // Verificar se é realmente um track de microfone ou câmera
      if (publication.kind === Track.Kind.Audio && publication.source === Track.Source.Microphone) {
        console.log('PageClientImpl: Track de microfone foi mutado');
        persistDeviceSettings({ audioEnabled: false });
      } else if (
        publication.kind === Track.Kind.Video &&
        publication.source === Track.Source.Camera
      ) {
        console.log('PageClientImpl: Track de câmera foi mutado');
        persistDeviceSettings({ videoEnabled: false });
      }
    };

    const handleTrackUnmuted = (publication: TrackPublication) => {
      // Ignorar eventos de screen sharing e outros sources que não são microfone ou câmera
      if (
        publication.source === Track.Source.ScreenShare ||
        publication.source === Track.Source.ScreenShareAudio ||
        publication.source === Track.Source.Unknown
      ) {
        console.log(
          'PageClientImpl: Ignorando evento TrackUnmuted para source:',
          publication.source,
        );
        return;
      }

      if (isMediaMutationSuppressed()) {
        console.log(
          'PageClientImpl: Ignorando TrackUnmuted transitório durante apply de fundo/processor',
        );
        return;
      }

      // Verificar se é realmente um track de microfone ou câmera
      if (publication.kind === Track.Kind.Audio && publication.source === Track.Source.Microphone) {
        console.log('PageClientImpl: Track de microfone foi desmutado');
        persistDeviceSettings({ audioEnabled: true });
      } else if (
        publication.kind === Track.Kind.Video &&
        publication.source === Track.Source.Camera
      ) {
        console.log('PageClientImpl: Track de câmera foi desmutado');
        persistDeviceSettings({ videoEnabled: true });
      }
    };

    participant.on(ParticipantEvent.TrackMuted, handleTrackMuted);
    participant.on(ParticipantEvent.TrackUnmuted, handleTrackUnmuted);

    return () => {
      participant.off(ParticipantEvent.TrackMuted, handleTrackMuted);
      participant.off(ParticipantEvent.TrackUnmuted, handleTrackUnmuted);
    };
  }, [room, persistDeviceSettings]);

  // Rastrear tentativas de screen share para evitar mutar microfone quando o usuário cancela
  // Isso funciona para TODAS as opções: Guia, Janela e Tela Inteira
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const trackScreenShareAttempt = () => {
      const screenShareBtn = document.querySelector('button[data-lk-source="screen_share"]');
      if (screenShareBtn) {
        const handleClick = () => {
          // Marcar que uma tentativa de screen share está em andamento
          // Limpar qualquer timeout anterior
          if (screenShareAttemptRef.current) {
            clearTimeout(screenShareAttemptRef.current);
          }
          
          // Criar um timeout de 10 segundos para limpar a referência
          // Isso cobre o tempo do diálogo do Chrome aparecer, o usuário escolher uma opção
          // (Guia, Janela ou Tela Inteira) e depois cancelar
          // 10 segundos é suficiente para qualquer interação com o diálogo
          screenShareAttemptRef.current = window.setTimeout(() => {
            screenShareAttemptRef.current = null;
          }, 10000);
        };

        screenShareBtn.addEventListener('click', handleClick);
        return () => {
          screenShareBtn.removeEventListener('click', handleClick);
        };
      }
    };

    // Tentar imediatamente
    const cleanup = trackScreenShareAttempt();

    // Se não encontrou, tentar novamente após um delay
    if (!cleanup) {
      const timeout = setTimeout(() => {
        trackScreenShareAttempt();
      }, 1000);
      return () => clearTimeout(timeout);
    }

    return cleanup;
  }, [room]);

  React.useEffect(() => {
    const handleSettingsSaved = (event: Event) => {
      const customEvent = event as CustomEvent<SavedSettings>;
      const detail = customEvent.detail;
      if (!detail) {
        return;
      }

      const { __source, ...rest } = detail;
      const previous = deviceSettingsRef.current;
      const normalized = normalizeBackground(rest, resolveCustomBackgroundPath);
      const nextSnapshot = {
        ...previous,
        audioEnabled: rest.audioEnabled ?? previous.audioEnabled,
        videoEnabled: rest.videoEnabled ?? previous.videoEnabled,
        audioDeviceId: rest.audioDeviceId ?? previous.audioDeviceId,
        videoDeviceId: rest.videoDeviceId ?? previous.videoDeviceId,
        audioOutputDeviceId: rest.audioOutputDeviceId ?? previous.audioOutputDeviceId,
        backgroundType: rest.backgroundType ?? normalized.type,
        virtualBackgroundImagePath: rest.virtualBackgroundImagePath ?? normalized.imagePath ?? null,
        __source,
      };
      const intent = classifySettingsSaveIntent(previous, nextSnapshot);
      latestBackgroundRef.current = normalized;
      deviceSettingsRef.current = nextSnapshot;

      if (intent === 'ignore') {
        return;
      }

      if (shouldApplyDeviceMediaFromSettings(intent)) {
        void applyDeviceSettings(deviceSettingsRef.current);
      }
      void applyBackgroundToLocalParticipant(normalized.type, normalized.imagePath);
    };

    window.addEventListener('settings-saved', handleSettingsSaved as EventListener);
    return () => {
      window.removeEventListener('settings-saved', handleSettingsSaved as EventListener);
    };
  }, [applyBackgroundToLocalParticipant, applyDeviceSettings, resolveCustomBackgroundPath]);

  React.useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const handleBackgroundFailsafe = () => {
      const plan = planBackgroundFailsafeRetry(latestBackgroundRef.current);
      if (plan.persistNone) {
        latestBackgroundRef.current = { type: 'none' };
        setVirtualBackgroundTypeRef.current('none');
        persistDeviceSettings({
          backgroundType: 'none',
          virtualBackgroundImagePath: null,
          __source: 'background-apply',
        });
        return;
      }
      if (!plan.retry) {
        return;
      }
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        const current = latestBackgroundRef.current;
        if (!current || current.type === 'none') {
          return;
        }
        void applyBackgroundToLocalParticipant(current.type, current.imagePath);
      }, plan.retryMs);
    };
    window.addEventListener(BACKGROUND_FAILSAFE_EVENT, handleBackgroundFailsafe);
    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      window.removeEventListener(BACKGROUND_FAILSAFE_EVENT, handleBackgroundFailsafe);
    };
  }, [applyBackgroundToLocalParticipant, persistDeviceSettings]);

  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'evolutto-user-settings' || !event.newValue) {
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue) as SavedSettings;
        const { __source, ...rest } = parsed;
        const previous = deviceSettingsRef.current;
        const normalized = normalizeBackground(rest, resolveCustomBackgroundPath);
        const nextSnapshot = {
          ...previous,
          audioEnabled: rest.audioEnabled ?? previous.audioEnabled,
          videoEnabled: rest.videoEnabled ?? previous.videoEnabled,
          audioDeviceId: rest.audioDeviceId ?? previous.audioDeviceId,
          videoDeviceId: rest.videoDeviceId ?? previous.videoDeviceId,
          audioOutputDeviceId: rest.audioOutputDeviceId ?? previous.audioOutputDeviceId,
          backgroundType: rest.backgroundType ?? normalized.type,
          virtualBackgroundImagePath:
            rest.virtualBackgroundImagePath ?? normalized.imagePath ?? null,
          __source,
        };
        const intent = classifySettingsSaveIntent(previous, nextSnapshot);
        latestBackgroundRef.current = normalized;
        deviceSettingsRef.current = nextSnapshot;

        if (shouldApplyDeviceMediaFromSettings(intent)) {
          void applyDeviceSettings(deviceSettingsRef.current);
        }
        void applyBackgroundToLocalParticipant(normalized.type, normalized.imagePath);
      } catch (error) {
        console.error('PageClientImpl: erro ao analisar configurações armazenadas:', error);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [applyBackgroundToLocalParticipant, applyDeviceSettings]);

  const lowPowerMode = useLowCPUOptimizer(room, {
    reducePublisherVideoQuality: true,
    reduceSubscriberVideoQuality: true,
    disableVideoProcessing: true,
  });
  useLargeRoomAdaptation(room);
  useSubscriberNetworkAdaptation(room);
  // "Não ouço o fulano": re-inscreve áudio remoto que parou de chegar / nunca anexou.
  useRemoteAudioWatchdog(room);
  React.useEffect(() => attachSubscriptionDiagnostics(room), [room]);
  useMeetingTelemetry(room, props.connectionDetails.roomName);

  React.useEffect(() => {
    if (!room || room.state !== 'connected') {
      return;
    }
    void applyVideoQualityToLocalCamera(room, videoQualityProfile);
  }, [room, videoQualityProfile]);

  React.useEffect(() => {
    if (!room) return;

    const onReconnecting = () => {
      setIsConnectionRecovering(true);
    };

    const onReconnected = () => {
      setIsConnectionRecovering(false);
    };

    room.on(RoomEvent.Reconnecting, onReconnecting);
    room.on(RoomEvent.Reconnected, onReconnected);

    return () => {
      room.off(RoomEvent.Reconnecting, onReconnecting);
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room]);

  const lowPowerToastShownRef = React.useRef(false);

  React.useEffect(() => {
    if (lowPowerMode && !lowPowerToastShownRef.current) {
      lowPowerToastShownRef.current = true;
      toast('Modo economia ativado — qualidade de vídeo reduzida para melhorar a performance.', {
        icon: '⚡',
        duration: 5000,
      });
    }
  }, [lowPowerMode]);


  // Só mostrar tela de desconexão quando estiver realmente desconectando
  if (isDisconnecting && !showFeedbackModal) {
    return (
      <div className="lk-room-container">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            color: 'var(--lk-fg)',
            fontSize: '18px',
          }}
        >
          Saindo da reunião...
        </div>
      </div>
    );
  }

  return (
    <MicrophonePermissionProvider value={microphonePermission}>
      <RoomErrorBoundary>
      <div className="lk-room-container">
        <ReconnectingOverlay isReconnecting={isConnectionRecovering} />
        <RoomContext.Provider value={room}>
          <AudioAutoplayBanner />
          <BreakoutProvider 
            isHost={props.isHost === true} 
            onRoomChange={props.onRoomChange}
          >
            <HandRaiseProvider>
              <HandRaiseQueueProvider>
                <ReactionsProvider>
                  <HostSettingsProvider forceHost={props.isHost === true}>
                    <HostControlProvider forceHost={props.isHost === true}>
                    <StreamProvider isHost={props.isHost === true}>
                      <PollProvider isHost={props.isHost === true}>
                        <VirtualBackgroundProvider>
                        <VirtualBackgroundSync onRegister={registerVirtualBackgroundSetter} />
                        <NoiseCancellationProvider>
                        <EconomyModeProvider>
                        <LayoutViewProvider>
                          <ScreenShareDefaultsBridge />
                          <EconomyModeRoomBridge
                            onClearVirtualBackground={() =>
                              clearVirtualBackgroundForEconomyRef.current()
                            }
                          />
                          <ConnectionStabilityMonitor />
                          <KeyboardShortcuts />
                          <EvoluVideoConference
                            chatMessageFormatter={formatChatMessageLinks}
                            SettingsComponent={SHOW_SETTINGS_MENU ? SettingsMenu : undefined}
                          />
                          <ControlBarAutoHide />
                          <InjectRecordIntoBar />
                          <InjectHandRaiseIntoBar />
                          <InjectParticipantsIntoBar />
                          <InjectMoreOptionsIntoBar />
                          <InjectReactionsIntoBar />
                          {/* <InjectLayoutIntoBar /> */}
                          <InjectCustomChatMessages />

                        <InjectPipButton />

                        <InjectPollButton />
                        <PollManagementModal />
                        <PollWidget />

                        <BreakoutControlPanel />
                        <BreakoutStatusBadge />

                        <ReactionsListener />
                        <ReactionsDisplay />
                        <DebugMode />
                        <RecordingIndicator />
                        <RecordingPreparingBanner />
                        <RecordingStartFailedNotice />
                        <RecordingRealtimeListener />
                        <MobileControlBar />
                        <HostControlFeedback />
                        <AudioActivationNotifier />

                        <InjectStreamButton />
                        <StreamControlModal />
                        </LayoutViewProvider>
                        </EconomyModeProvider>
                        </NoiseCancellationProvider>
                        </VirtualBackgroundProvider>
                      </PollProvider>
                      </StreamProvider>
                    </HostControlProvider>
                  </HostSettingsProvider>
                </ReactionsProvider>
              </HandRaiseQueueProvider>
            </HandRaiseProvider>
          </BreakoutProvider>
        </RoomContext.Provider>
        <MicrophonePermissionEffects />
        <PostMeetingFeedbackModal
          open={showFeedbackModal}
          formUrl={FEEDBACK_FORM_URL}
          onClose={handleFeedbackModalClose}
        />
      </div>
      </RoomErrorBoundary>
    </MicrophonePermissionProvider>
  );
}